// Loyalty tier definitions — single source of truth for the admin CRM view
// and the customer account page. Tiers are based on LIFETIME EARNED points
// (the running total of all positive ledger entries, never reduced by
// redemptions) so a customer keeps their tier once earned.
//
// Earn/redeem mechanics live in base44/shared/loyalty.ts (1 pt/£1, 100 pts = £1).
// These tiers are an entitlement ladder: each tier unlocks a benefit the
// customer is entitled to once their lifetime spend crosses the threshold.

export const LOYALTY_TIERS = [
  {
    id: 'bronze',
    name: 'Bronze',
    threshold: 0,
    color: '#b08d57',
    benefit: 'Standard member — earn 1 point per £1 spent on bookings & shop.',
  },
  {
    id: 'silver',
    name: 'Silver',
    threshold: 1000,
    color: '#9ca3af',
    benefit: '5% bonus points on every purchase + early access to new experiences.',
  },
  {
    id: 'gold',
    name: 'Gold',
    threshold: 3000,
    color: '#eab308',
    benefit: 'Priority booking windows + 10% bonus points on every purchase.',
  },
  {
    id: 'platinum',
    name: 'Platinum',
    threshold: 7500,
    color: '#a78bfa',
    benefit: '1 free session per month + 15% bonus points + priority support line.',
  },
];

// Returns the highest tier the customer has reached for a given lifetime
// earned points total.
export function getTierForPoints(lifetimeEarned) {
  let current = LOYALTY_TIERS[0];
  for (const t of LOYALTY_TIERS) {
    if (lifetimeEarned >= t.threshold) current = t;
  }
  return current;
}

// Returns the next tier above the current one (null if at the top tier).
export function getNextTier(lifetimeEarned) {
  const current = getTierForPoints(lifetimeEarned);
  const idx = LOYALTY_TIERS.findIndex((t) => t.id === current.id);
  return idx < LOYALTY_TIERS.length - 1 ? LOYALTY_TIERS[idx + 1] : null;
}

// Progress to the next tier as { current, next, progress, pointsToNext }.
export function getTierProgress(lifetimeEarned) {
  const current = getTierForPoints(lifetimeEarned);
  const next = getNextTier(lifetimeEarned);
  if (!next) {
    return { current, next: null, progress: 100, pointsToNext: 0 };
  }
  const span = next.threshold - current.threshold;
  const into = lifetimeEarned - current.threshold;
  return {
    current,
    next,
    progress: Math.min(100, Math.round((into / span) * 100)),
    pointsToNext: Math.max(0, next.threshold - lifetimeEarned),
  };
}